import { ReportData } from "../types/global.d";

type SortOrder = "asc" | "desc";
type TimestampValue = string | number | Date | null | undefined;

const REPORT_STATUS = {
  REPORT_ONLY: 1,
  REPORT_VERIFIED: 2,
  REPORT_WITH_CLEANING: 4,
  VERIFIED_REPORT_WITH_CLEANING: 5,
  CLEANING_VERIFIED_ONLY: 7,
  FULLY_COMPLETED: 8,
} as const;

const PENDING_REPORT_STATUSES = new Set<number>([
  REPORT_STATUS.REPORT_ONLY,
  REPORT_STATUS.REPORT_WITH_CLEANING,
  REPORT_STATUS.CLEANING_VERIFIED_ONLY,
]);

const PENDING_CLEANUP_STATUSES = new Set<number>([
  REPORT_STATUS.REPORT_WITH_CLEANING,
  REPORT_STATUS.VERIFIED_REPORT_WITH_CLEANING,
]);

const getNumericStatus = (report: ReportData): number | null => {
  const numericStatus = Number(report.status);
  return Number.isFinite(numericStatus) ? numericStatus : null;
};

const parseNumericTimestamp = (value: number): number | null => {
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  return value;
};

const parseUtcDateParts = (
  year: string,
  month: string,
  day: string,
  hour = "0",
  minute = "0",
  second = "0"
): number | null => {
  const dateParts = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };

  const timestamp = Date.UTC(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day,
    dateParts.hour,
    dateParts.minute,
    dateParts.second
  );
  const parsedDate = new Date(timestamp);

  if (
    parsedDate.getUTCFullYear() !== dateParts.year ||
    parsedDate.getUTCMonth() !== dateParts.month - 1 ||
    parsedDate.getUTCDate() !== dateParts.day ||
    parsedDate.getUTCHours() !== dateParts.hour ||
    parsedDate.getUTCMinutes() !== dateParts.minute ||
    parsedDate.getUTCSeconds() !== dateParts.second
  ) {
    return null;
  }

  return parseNumericTimestamp(timestamp);
};

export const parseReportSortTimestamp = (
  value: TimestampValue
): number | null => {
  if (value instanceof Date) {
    return parseNumericTimestamp(value.getTime());
  }

  if (typeof value === "number") {
    return parseNumericTimestamp(value);
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const numericValue = Number(trimmed);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return numericValue;
  }

  const ddmmyyyyMatch = trimmed.match(
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (ddmmyyyyMatch) {
    const [, day, month, year, hour = "0", minute = "0", second = "0"] =
      ddmmyyyyMatch;
    return parseUtcDateParts(year, month, day, hour, minute, second);
  }

  const exifDateMatch = trimmed.match(
    /^(\d{4}):(\d{2}):(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (exifDateMatch) {
    const [, year, month, day, hour = "0", minute = "0", second = "0"] =
      exifDateMatch;
    return parseUtcDateParts(year, month, day, hour, minute, second);
  }

  const timestamp = Date.parse(trimmed);
  return parseNumericTimestamp(timestamp);
};

const getFirstValidTimestamp = (
  values: TimestampValue[]
): number | null => {
  for (const value of values) {
    const timestamp = parseReportSortTimestamp(value);
    if (timestamp !== null) {
      return timestamp;
    }
  }

  return null;
};

export const getReportSortTimestamp = (report: ReportData): number | null => {
  const payloadSortTimestamp = parseReportSortTimestamp(report.sortTimestamp);
  if (payloadSortTimestamp !== null) {
    return payloadSortTimestamp;
  }

  const cleaned = report.cleaned;

  if (cleaned) {
    return getFirstValidTimestamp([
      cleaned.date,
      cleaned.createdAt,
      report.date,
      report.createdAt,
    ]);
  }

  return getFirstValidTimestamp([report.date, report.createdAt]);
};

export const hasPendingReportAcceptance = (report: ReportData): boolean => {
  const status = getNumericStatus(report);

  if (status !== null) {
    return PENDING_REPORT_STATUSES.has(status);
  }

  return report.verified === false;
};

export const hasPendingCleanupAcceptance = (report: ReportData): boolean => {
  const status = getNumericStatus(report);

  if (status !== null) {
    return PENDING_CLEANUP_STATUSES.has(status);
  }

  return !!report.cleaned && report.cleaned.verified === false;
};

export const getAdminModerationPriority = (report: ReportData): number => {
  return hasPendingReportAcceptance(report) ||
    hasPendingCleanupAcceptance(report)
    ? 0
    : 1;
};

export const compareReportsByTimestamp = (
  a: ReportData,
  b: ReportData,
  order: SortOrder = "desc"
): number => {
  const timeA = getReportSortTimestamp(a);
  const timeB = getReportSortTimestamp(b);

  if (timeA === null && timeB === null) {
    return a._id.localeCompare(b._id);
  }
  if (timeA === null) {
    return 1;
  }
  if (timeB === null) {
    return -1;
  }

  const diff = order === "asc" ? timeA - timeB : timeB - timeA;
  return diff || a._id.localeCompare(b._id);
};

export const compareReportsByDefaultPriority = (
  a: ReportData,
  b: ReportData,
  isAdmin = false
): number => {
  if (isAdmin) {
    const priorityDiff =
      getAdminModerationPriority(a) - getAdminModerationPriority(b);

    if (priorityDiff !== 0) {
      return priorityDiff;
    }
  }

  return compareReportsByTimestamp(a, b, "desc");
};

export const sortReportsByDefaultPriority = (
  reports: ReportData[],
  isAdmin = false
): ReportData[] => {
  return [...reports].sort((a, b) =>
    compareReportsByDefaultPriority(a, b, isAdmin)
  );
};
